/**
 * Token manager: the framework-free core of the auth stack.
 *
 * Owns the in-memory access token, the persisted rotating refresh token, the
 * single-flight refresh, and the global 401 interceptor with its
 * queue-and-replay behaviour. AuthContext installs the interceptor once and
 * reacts to the handler callbacks; everything here is unit-testable without
 * React. See docs/technical/FRONTEND_ARCHITECTURE.md#authentication-system.
 *
 * Multi-tab notes: the refresh token lives in localStorage (shared across
 * tabs), rotation happens under a Web Lock where available, and the stored
 * token is re-read inside the lock so a tab that lost the race adopts the
 * winner's rotation instead of tripping reuse detection.
 */
import { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';

export type TrustTier = 'untrusted' | 'moderate' | 'trusted';

export interface SessionInfo {
  id: string;
  trust_tier: TrustTier;
  device_label: string | null;
  user_agent: string | null;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface TokenPairResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_in: number;
  session: SessionInfo;
}

export interface TokenManagerHandlers {
  /** Silent refresh is impossible/failed — open the re-auth dialog. */
  onReauthRequired: () => void;
  /** Refresh-token reuse detected server-side — security logout. */
  onHardLogout: (reason: 'reuse_detected') => void;
  /** A refresh rotated the pair — update headers/timers/session display. */
  onTokenRefreshed: (pair: TokenPairResponse) => void;
}

export const REFRESH_TOKEN_KEY = 'kn.refreshToken';
export const TRUST_TIER_HINT_KEY = 'kn.trustTierHint';

/** TTL choices per tier — mirror of the server's TIER_TTL_CHOICES. */
export const TIER_TTL_OPTIONS: Record<TrustTier, { minutes: number; label: string }[]> = {
  untrusted: [
    { minutes: 20, label: '20 minutes' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 60, label: '1 hour' },
  ],
  moderate: [
    { minutes: 600, label: '10 hours' },
    { minutes: 1440, label: '24 hours' },
  ],
  trusted: [{ minutes: 129600, label: '90 days' }],
};

export const TRUST_TIER_LABELS: Record<TrustTier, string> = {
  untrusted: 'Shared device',
  moderate: 'Work device',
  trusted: 'My device',
};

// Requests to these endpoints must never trigger the refresh flow — a 401
// from them *is* the final answer.
const AUTH_URL_PATTERN = /\/api\/auth\/(login|login-json|register|refresh|logout)$/;

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
}

interface PendingRequest {
  config: RetriableConfig;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

let installed = false;
let axiosRef: AxiosInstance | null = null;
let handlers: TokenManagerHandlers | null = null;
let apiBaseUrl = '';

let accessToken: string | null = null;
let accessExpiresAt = 0; // epoch ms; 0 = unknown
let refreshPromise: Promise<TokenPairResponse> | null = null;
let refreshIsExtend = false;
let pendingQueue: PendingRequest[] = [];
// Bumped on logout so refreshes/replies that straddle a logout are discarded.
let sessionEpoch = 0;

export const getAccessToken = (): string | null => accessToken;
export const getAccessExpiresAt = (): number => accessExpiresAt;

export const setAccessToken = (token: string | null, expiresInSeconds?: number): void => {
  accessToken = token;
  accessExpiresAt = token && expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;
};

export const getStoredRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const storeRefreshToken = (token: string | null): void => {
  try {
    if (token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
  } catch {
    // Storage unavailable — the session simply won't survive a reload.
  }
};

const setAuthHeader = (config: RetriableConfig, token: string): void => {
  if (!config.headers) {
    config.headers = {} as RetriableConfig['headers'];
  }
  (config.headers as Record<string, unknown>).Authorization = `Bearer ${token}`;
};

const refreshFailureDetail = (error: unknown): string | null => {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof detail === 'string' ? detail : null;
};

export const isReuseDetected = (error: unknown): boolean =>
  refreshFailureDetail(error) === 'refresh_token_reused';

const doRefresh = async (extend: boolean, epoch: number): Promise<TokenPairResponse> => {
  // Re-read inside the (possible) lock: another tab may have rotated first.
  const stored = getStoredRefreshToken();
  if (!stored) {
    throw new Error('no_refresh_token');
  }
  if (!axiosRef) {
    throw new Error('token manager not installed');
  }
  const response = await axiosRef.post(`${apiBaseUrl}/api/auth/refresh`, {
    refresh_token: stored,
    extend,
  });
  const pair = response.data as TokenPairResponse;
  // A logout that landed while this request was in flight ends the session:
  // persisting the rotated token or restoring the auth header here would
  // resurrect it (and on a shared device could attach it to whoever signs in
  // next). Check BEFORE any side effect, not after.
  if (epoch !== sessionEpoch) {
    throw new Error('session_ended');
  }
  // Persist the rotation BEFORE resolving so no caller can observe the old
  // (now revoked) token.
  storeRefreshToken(pair.refresh_token);
  setAccessToken(pair.access_token, pair.expires_in);
  handlers?.onTokenRefreshed(pair);
  return pair;
};

const withRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = (navigator as { locks?: { request?: Function } }).locks;
  if (locks?.request) {
    return locks.request('kn.refresh', fn) as Promise<T>;
  }
  return fn();
};

/**
 * Single-flight refresh: N concurrent callers share one network request.
 * Rejects if a logout happens while the refresh is in flight.
 */
export const refreshAccessToken = (extend = false): Promise<TokenPairResponse> => {
  if (refreshPromise) {
    if (!extend || refreshIsExtend) {
      return refreshPromise;
    }
    // An "Extend" must not be swallowed by an in-flight plain refresh — that
    // would dismiss the expiry warning without moving the deadline. Wait for
    // the current flight, then run a real extend refresh.
    return refreshPromise
      .catch(() => undefined)
      .then(() => refreshAccessToken(true));
  }
  const epoch = sessionEpoch;
  refreshIsExtend = extend;
  const promise = withRefreshLock(() => doRefresh(extend, epoch)).finally(() => {
    if (refreshPromise === promise) {
      refreshPromise = null;
      refreshIsExtend = false;
    }
  });
  refreshPromise = promise;
  return promise;
};

const enqueueForReauth = (config: RetriableConfig): Promise<unknown> => {
  const promise = new Promise((resolve, reject) => {
    pendingQueue.push({ config, resolve, reject });
  });
  handlers?.onReauthRequired();
  return promise;
};

/** Replay every parked request with a fresh access token (post re-auth). */
export const replayQueue = (freshAccessToken: string): void => {
  const queue = pendingQueue;
  pendingQueue = [];
  queue.forEach(({ config, resolve, reject }) => {
    config._retried = true;
    setAuthHeader(config, freshAccessToken);
    if (!axiosRef) {
      reject(new Error('token manager not installed'));
      return;
    }
    // The original config is re-dispatched verbatim: method, url, data and
    // headers all survive — only Authorization is stamped fresh.
    axiosRef.request(config).then(resolve, reject);
  });
};

export const rejectQueue = (reason: unknown): void => {
  const queue = pendingQueue;
  pendingQueue = [];
  queue.forEach(({ reject }) => reject(reason));
};

export const pendingQueueLength = (): number => pendingQueue.length;

/** Current logout epoch — callers use it to detect a logout mid-request. */
export const getSessionEpoch = (): number => sessionEpoch;

/** Logout: invalidate in-flight refreshes, drop tokens, fail parked requests. */
export const resetForLogout = (): void => {
  sessionEpoch += 1;
  refreshPromise = null;
  setAccessToken(null);
  storeRefreshToken(null);
  rejectQueue(new Error('logged_out'));
};

export const installAuthInterceptor = (
  axiosInstance: AxiosInstance,
  baseUrl: string,
  h: TokenManagerHandlers,
): void => {
  // Handlers are always refreshed; the interceptor itself is registered once
  // (StrictMode double-mounts must not stack interceptors).
  handlers = h;
  axiosRef = axiosInstance;
  apiBaseUrl = baseUrl;
  if (installed) {
    return;
  }
  installed = true;

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetriableConfig | undefined;
      const status = error.response?.status;
      const url = config?.url || '';
      if (status !== 401 || !config || AUTH_URL_PATTERN.test(url)) {
        return Promise.reject(error);
      }
      if (config._retried) {
        // A freshly-refreshed token still got 401 — don't loop; park it.
        return enqueueForReauth(config);
      }
      const epoch = sessionEpoch;
      try {
        const pair = await refreshAccessToken();
        if (epoch !== sessionEpoch) {
          return Promise.reject(error);
        }
        config._retried = true;
        setAuthHeader(config, pair.access_token);
        return axiosRef!.request(config);
      } catch (refreshError) {
        if (epoch !== sessionEpoch) {
          return Promise.reject(error);
        }
        if (isReuseDetected(refreshError)) {
          handlers?.onHardLogout('reuse_detected');
          return Promise.reject(error);
        }
        return enqueueForReauth(config);
      }
    },
  );
};

/** Test hook: wipe module state so each test starts from a clean slate. */
export const __resetTokenManagerForTests = (): void => {
  installed = false;
  axiosRef = null;
  handlers = null;
  apiBaseUrl = '';
  accessToken = null;
  accessExpiresAt = 0;
  refreshPromise = null;
  refreshIsExtend = false;
  pendingQueue = [];
  sessionEpoch = 0;
};
